import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// ── BUDGETS PAGE ────────────────────────────────────────────────────────────
export function Budgets() {
  const [month, setMonth] = useState(() => new Date().toISOString().substring(0,7));
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [cats, bud] = await Promise.all([
      api.get('/categories'), api.get(`/budgets?month=${month}`)
    ]);
    setCategories(cats.data);
    const map = {};
    bud.data.forEach(b => map[b.category_id] = parseFloat(b.amount_usd)||0);
    setBudgets(map);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const arr = categories.map(c => ({ category_id: c.id, amount_usd: budgets[c.id]||0 }));
      await api.post('/budgets/upsert', { month_key: month, budgets: arr });
      toast.success('Budgets saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleCopyPrev = async () => {
    const [y,m] = month.split('-').map(Number);
    const prev = new Date(y,m-2,1);
    const fromKey = `${prev.getFullYear()}-${String(prev.getMonth()+1).padStart(2,'0')}`;
    try {
      await api.post('/budgets/copy', { from_month: fromKey, to_month: month });
      toast.success(`Copied from ${fromKey}`); load();
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, color:'#1E2A38', fontWeight:700 }}>🎯 Monthly Budgets</h1>
          <p style={{ margin:'4px 0 0', color:'#888', fontSize:13 }}>Set spending limits per category</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <input type="month" value={month} onChange={e=>setMonth(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13 }} />
          <button onClick={handleCopyPrev}
            style={{ padding:'9px 14px', borderRadius:8, border:'1.5px solid #ddd', background:'#fff', fontSize:13, cursor:'pointer' }}>
            📋 Copy Previous Month
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding:'9px 18px', borderRadius:8, border:'none',
              background:'#1E2A38', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {saving ? 'Saving…' : '💾 Save Budgets'}
          </button>
        </div>
      </div>
      <div style={{ background:'#fff', borderRadius:12, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
          {categories.map(cat => (
            <div key={cat.id} style={{ display:'flex', alignItems:'center', gap:12,
              padding:'12px 16px', borderRadius:8, border:'1.5px solid #eee' }}>
              <div style={{ fontSize:22 }}>{cat.icon}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#444', marginBottom:6 }}>{cat.name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:12, color:'#888' }}>$</span>
                  <input type="number" step="1" min="0" value={budgets[cat.id]||''}
                    onChange={e=>setBudgets(b=>({...b,[cat.id]:e.target.value}))}
                    placeholder="0"
                    style={{ flex:1, padding:'6px 10px', borderRadius:6, border:'1.5px solid #ddd', fontSize:13 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop:20, padding:'12px 16px', background:'#F0F4F8', borderRadius:8, fontSize:13, color:'#666' }}>
          💡 Tip: Blue cells are inputs. Budgets auto-appear in your Dashboard and Monthly Reports.
        </div>
      </div>
    </div>
  );
}

// ── SAVINGS GOALS PAGE ──────────────────────────────────────────────────────
export function Goals() {
  const [goals, setGoals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = { name:'', target_usd:'', saved_usd:'', target_date:'', color:'#2ECC71', icon:'🎯', notes:'' };
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get('/goals').then(r=>setGoals(r.data)).catch(()=>toast.error('Failed'));

  useEffect(()=>{ load(); },[]);

  const handleSave = async () => {
    try {
      if (editing) { await api.put(`/goals/${editing}`, form); toast.success('Updated'); }
      else { await api.post('/goals', form); toast.success('Goal added'); }
      setShowForm(false); load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this goal?')) return;
    await api.delete(`/goals/${id}`); toast.success('Deleted'); load();
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, color:'#1E2A38', fontWeight:700 }}>🏦 Savings Goals</h1>
          <p style={{ margin:'4px 0 0', color:'#888', fontSize:13 }}>Track progress toward your targets</p>
        </div>
        <button onClick={()=>{ setForm(EMPTY); setEditing(null); setShowForm(true); }}
          style={{ padding:'9px 18px', borderRadius:8, border:'none', background:'#1E2A38',
            color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>+ Add Goal</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
        {goals.map(g => {
          const pct = g.target_usd>0 ? Math.min(parseFloat(g.saved_usd)/parseFloat(g.target_usd),1) : 0;
          const rem = parseFloat(g.target_usd) - parseFloat(g.saved_usd);
          const daysLeft = g.target_date ? Math.ceil((new Date(g.target_date)-new Date())/86400000) : null;
          return (
            <div key={g.id} style={{ background:'#fff', borderRadius:12, padding:20,
              boxShadow:'0 2px 8px rgba(0,0,0,0.07)', borderTop:`4px solid ${g.color||'#2ECC71'}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:22 }}>{g.icon}</div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#1E2A38', marginTop:4 }}>{g.name}</div>
                </div>
                {g.is_achieved && <span style={{ fontSize:20 }}>✅</span>}
              </div>
              <div style={{ fontSize:20, fontWeight:700, color:g.color||'#2ECC71' }}>
                ${Number(g.saved_usd).toLocaleString()}
                <span style={{ fontSize:12, color:'#aaa', fontWeight:400 }}> / ${Number(g.target_usd).toLocaleString()}</span>
              </div>
              <div style={{ height:8, background:'#f0f0f0', borderRadius:4, margin:'12px 0 6px' }}>
                <div style={{ height:8, borderRadius:4, width:`${pct*100}%`,
                  background:g.color||'#2ECC71', transition:'width 0.3s' }}/>
              </div>
              <div style={{ fontSize:11, color:'#888', marginBottom:12 }}>
                {(pct*100).toFixed(1)}% complete — ${Number(rem).toLocaleString()} remaining
                {daysLeft!==null && ` — ${daysLeft>0?`${daysLeft} days left`:'⚠️ Past due'}`}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={()=>{ setForm({...g,target_usd:g.target_usd,saved_usd:g.saved_usd,target_date:g.target_date?.substring(0,10)||''}); setEditing(g.id); setShowForm(true); }}
                  style={{ flex:1, padding:'6px', borderRadius:6, border:'1px solid #ddd', background:'#fff', fontSize:12, cursor:'pointer' }}>✏️ Edit</button>
                <button onClick={()=>handleDelete(g.id)}
                  style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #fcc', background:'#fff', color:'#E74C3C', fontSize:12, cursor:'pointer' }}>🗑️</button>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:440 }}>
            <h2 style={{ margin:'0 0 20px', fontSize:17 }}>{editing?'✏️ Edit Goal':'+ New Goal'}</h2>
            {[['name','Goal Name','text'],['target_usd','Target Amount ($)','number'],
              ['saved_usd','Saved So Far ($)','number'],['target_date','Target Date','date']].map(([k,l,t])=>(
              <div key={k} style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>{l}</label>
                <input type={t} value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}
                  style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13, boxSizing:'border-box' }} />
              </div>
            ))}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Icon</label>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {['🎯','🏠','✈️','🚗','💊','📚','💍','🎓','🏦','🎁'].map(ico=>(
                  <button key={ico} onClick={()=>setForm(f=>({...f,icon:ico}))}
                    style={{ fontSize:20, padding:'4px 8px', borderRadius:6, border:`2px solid ${form.icon===ico?'#1E2A38':'#eee'}`, background:'#fff', cursor:'pointer' }}>
                    {ico}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display:'flex', gap:12, marginTop:20, justifyContent:'flex-end' }}>
              <button onClick={()=>setShowForm(false)}
                style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid #ddd', background:'#fff', fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button onClick={handleSave}
                style={{ padding:'9px 18px', borderRadius:8, border:'none', background:'#1E2A38', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                {editing?'Save':'Add Goal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── RECURRING PAGE ──────────────────────────────────────────────────────────
export function Recurring() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = { description:'', type:'Expense', category_id:'', frequency:'Monthly',
    amount_usd:'', amount_lbp:'', currency:'USD', account:'', start_date:'', next_due:'', is_active:true, notes:'' };
  const [form, setForm] = useState(EMPTY);

  const load = () => Promise.all([api.get('/recurring'), api.get('/categories')])
    .then(([r,c])=>{ setItems(r.data); setCategories(c.data); });

  useEffect(()=>{ load(); },[]);

  const handleSave = async () => {
    try {
      if (editing) { await api.put(`/recurring/${editing}`, form); toast.success('Updated'); }
      else { await api.post('/recurring', form); toast.success('Added'); }
      setShowForm(false); load();
    } catch { toast.error('Failed'); }
  };

  const monthly = items.filter(i=>i.is_active&&i.frequency==='Monthly').reduce((s,i)=>s+parseFloat(i.amount_usd),0);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, color:'#1E2A38', fontWeight:700 }}>🔁 Recurring</h1>
          <p style={{ margin:'4px 0 0', color:'#888', fontSize:13 }}>Monthly recurring total (active): <strong>${monthly.toLocaleString()}</strong></p>
        </div>
        <button onClick={()=>{ setForm(EMPTY); setEditing(null); setShowForm(true); }}
          style={{ padding:'9px 18px', borderRadius:8, border:'none', background:'#1E2A38', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          + Add Recurring
        </button>
      </div>
      <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'#1E2A38' }}>
              {['Description','Type','Category','Frequency','Amount','Next Due','Active',''].map((h,i)=>(
                <th key={i} style={{ padding:'10px 12px', color:'#fff', fontSize:11, fontWeight:600, textAlign:'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it,i) => {
              const typeColor = it.type==='Income'?'#27AE60':it.type==='Savings'?'#8E44AD':'#E74C3C';
              return (
                <tr key={it.id} style={{ background:i%2===0?'#F8FAFC':'#fff', borderBottom:'1px solid #f0f0f0', opacity:it.is_active?1:0.5 }}>
                  <td style={{ padding:'9px 12px', fontSize:13 }}>{it.description}</td>
                  <td style={{ padding:'9px 12px' }}>
                    <span style={{ background:typeColor+'22', color:typeColor, borderRadius:12, padding:'2px 8px', fontSize:11, fontWeight:600 }}>{it.type}</span>
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12 }}>{it.category_icon} {it.category_name}</td>
                  <td style={{ padding:'9px 12px', fontSize:12 }}>{it.frequency}</td>
                  <td style={{ padding:'9px 12px', fontSize:13, fontWeight:600, color:typeColor }}>
                    ${Number(it.amount_usd).toLocaleString()}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12, color:'#666' }}>
                    {it.next_due ? new Date(it.next_due).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12 }}>
                    <span style={{ color:it.is_active?'#27AE60':'#aaa' }}>{it.is_active?'✅ Yes':'⏸️ No'}</span>
                  </td>
                  <td style={{ padding:'9px 12px' }}>
                    <button onClick={()=>{ setForm({...it,start_date:it.start_date?.substring(0,10)||'',next_due:it.next_due?.substring(0,10)||''}); setEditing(it.id); setShowForm(true); }}
                      style={{ padding:'4px 10px', fontSize:11, borderRadius:6, border:'1px solid #ddd', background:'#fff', cursor:'pointer' }}>✏️</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:500, maxHeight:'90vh', overflowY:'auto' }}>
            <h2 style={{ margin:'0 0 20px', fontSize:17 }}>{editing?'✏️ Edit Recurring':'+ New Recurring'}</h2>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {[['description','Description','text'],['amount_usd','Amount USD','number'],
                ['start_date','Start Date','date'],['next_due','Next Due','date']].map(([k,l,t])=>(
                <div key={k} style={{ gridColumn:k==='description'?'1/-1':undefined }}>
                  <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>{l}</label>
                  <input type={t} value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}
                    style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13, boxSizing:'border-box' }} />
                </div>
              ))}
              {[['type','Type',['Income','Expense','Savings']],
                ['frequency','Frequency',['Monthly','Weekly','Bi-Weekly','Quarterly','Annual']],
                ['currency','Currency',['USD','LBP']],
                ['account','Account',['','Cash','Bank','Credit Card']]].map(([k,l,opts])=>(
                <div key={k}>
                  <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>{l}</label>
                  <select value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}
                    style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13 }}>
                    {opts.map(o=><option key={o} value={o}>{o||'— Select —'}</option>)}
                  </select>
                </div>
              ))}
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Category</label>
                <select value={form.category_id||''} onChange={e=>setForm(f=>({...f,category_id:e.target.value}))}
                  style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13 }}>
                  <option value="">— Select —</option>
                  {categories.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </select>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:20 }}>
                <input type="checkbox" id="active" checked={form.is_active}
                  onChange={e=>setForm(f=>({...f,is_active:e.target.checked}))} />
                <label htmlFor="active" style={{ fontSize:13 }}>Active</label>
              </div>
            </div>
            <div style={{ display:'flex', gap:12, marginTop:20, justifyContent:'flex-end' }}>
              <button onClick={()=>setShowForm(false)}
                style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid #ddd', background:'#fff', fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button onClick={handleSave}
                style={{ padding:'9px 18px', borderRadius:8, border:'none', background:'#1E2A38', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                {editing?'Save':'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SETTINGS PAGE ───────────────────────────────────────────────────────────
export function Settings() {
  const [fx, setFx] = useState([]);
  const [cycleDay, setCycleDay] = useState(26);
  const [pwForm, setPwForm] = useState({ currentPassword:'', newPassword:'', confirm:'' });
  const [savingFx, setSavingFx] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(()=>{
    api.get('/fx').then(r=>setFx(r.data));
    api.get('/dashboard/settings').then(r=>setCycleDay(parseInt(r.data.cycle_start_day)||26));
  },[]);

  const handleFxSave = async () => {
    setSavingFx(true);
    try {
      await Promise.all(fx.map(f=>api.put(`/fx/${f.currency}`,{rate_to_usd:f.rate_to_usd})));
      toast.success('FX rates updated');
    } catch { toast.error('Failed'); }
    finally { setSavingFx(false); }
  };

  const handlePasswordChange = async () => {
    if (pwForm.newPassword !== pwForm.confirm) return toast.error('Passwords do not match');
    if (pwForm.newPassword.length < 8) return toast.error('Minimum 8 characters');
    setSavingPw(true);
    try {
      await api.post('/auth/change-password', { currentPassword:pwForm.currentPassword, newPassword:pwForm.newPassword });
      toast.success('Password changed'); setPwForm({ currentPassword:'', newPassword:'', confirm:'' });
    } catch (err) { toast.error(err.response?.data?.error||'Failed'); }
    finally { setSavingPw(false); }
  };

  return (
    <div>
      <h1 style={{ margin:'0 0 24px', fontSize:22, color:'#1E2A38', fontWeight:700 }}>⚙️ Settings</h1>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* Cycle Settings */}
        <div style={{ background:'#fff', borderRadius:12, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.07)', gridColumn:'1/-1' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15 }}>📅 Pay Cycle Settings</h3>
          <p style={{ margin:'0 0 16px', color:'#666', fontSize:13 }}>
            Define which day of the month your income cycle starts. e.g. if you get paid on the 26th, set 26.
            Your cycle will run from the <strong>{cycleDay}th</strong> of each month to the <strong>{cycleDay-1}th</strong> of the next.
          </p>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Cycle Start Day</label>
              <input type="number" min="1" max="28" value={cycleDay} onChange={e=>setCycleDay(parseInt(e.target.value))}
                style={{ width:80, padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:14, fontWeight:700 }} />
            </div>
            <div style={{ fontSize:13, color:'#888', paddingTop:20 }}>
              → Your cycle: <strong>26th May – 25th June</strong>, <strong>26th June – 25th July</strong>, etc.
            </div>
            <button onClick={async()=>{
              try {
                await api.put('/dashboard/settings',{cycle_start_day:cycleDay});
                toast.success('Cycle day saved');
              } catch { toast.error('Failed'); }
            }} style={{ marginTop:16, padding:'9px 18px', borderRadius:8, border:'none',
              background:'#1E2A38', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              💾 Save
            </button>
          </div>
        </div>
        {/* FX Rates */}
        <div style={{ background:'#fff', borderRadius:12, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15 }}>💱 FX Rates (Base: USD)</h3>
          {fx.map((f,i)=>(
            <div key={f.currency} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
              <div style={{ width:40, fontWeight:700, fontSize:13, color:'#444' }}>{f.currency}</div>
              <div style={{ flex:1, fontSize:12, color:'#888' }}>{f.currency_name}</div>
              <input type="number" step="any" value={f.rate_to_usd}
                onChange={e=>setFx(arr=>arr.map((r,j)=>j===i?{...r,rate_to_usd:e.target.value}:r))}
                style={{ width:120, padding:'6px 10px', borderRadius:6, border:'1.5px solid #ddd', fontSize:13 }} />
            </div>
          ))}
          <p style={{ fontSize:11, color:'#aaa', margin:'8px 0 12px' }}>Rate = how many USD per 1 unit of currency. e.g. 1 EUR = 1.085 USD</p>
          <button onClick={handleFxSave} disabled={savingFx}
            style={{ padding:'9px 18px', borderRadius:8, border:'none', background:'#1E2A38',
              color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {savingFx?'Saving…':'💾 Save Rates'}
          </button>
        </div>

        {/* Password */}
        <div style={{ background:'#fff', borderRadius:12, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15 }}>🔐 Change Password</h3>
          {[['currentPassword','Current Password'],['newPassword','New Password'],['confirm','Confirm New Password']].map(([k,l])=>(
            <div key={k} style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>{l}</label>
              <input type="password" value={pwForm[k]} onChange={e=>setPwForm(f=>({...f,[k]:e.target.value}))}
                style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13, boxSizing:'border-box' }} />
            </div>
          ))}
          <button onClick={handlePasswordChange} disabled={savingPw}
            style={{ padding:'9px 18px', borderRadius:8, border:'none', background:'#E74C3C',
              color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {savingPw?'Saving…':'🔒 Change Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CYCLE SETTINGS addition to Settings page ─────────────────────────────
// This is already merged into the Settings export above
